import { c as _c } from "react/compiler-runtime";
import { useToggle } from '@react-hookz/web/useToggle/index.js';
import type { Route } from 'next';
import { usePathname } from 'next/navigation';
import type { HTMLAttributeAnchorTarget, PropsWithChildren, ReactNode } from 'react';
import { Link } from '#components/Link/Link';
import { NavigationButton } from '#components/base/NavigationButton';
import { IconSystem } from '#icons/IconSystem';
import { NavigationList } from './NavigationList';
import styles from './NavigationListItem.module.scss';
type NavigationListLinkItemProps = PropsWithChildren<{
  depth: number;
  href: Route;
  target?: HTMLAttributeAnchorTarget | undefined;
  icon?: ReactNode;
}>;
type NavigationListNodeItemProps = PropsWithChildren<{
  depth: number;
  title: string;
  className?: string;
}>;
type NavigationListCustomItemProps = PropsWithChildren<{
  className?: string;
  depth: number;
}>;
function NavigationListItem(t0) {
  const $ = _c(3);
  const {
    children,
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const t2 = `${styles.navigationListItem} ${className}`;
  let t3;
  if ($[0] !== children || $[1] !== t2) {
    t3 = <li className={t2} role="menuitem">{children}</li>;
    $[0] = children;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return t3;
}
export function NavigationListLinkItem(t0) {
  const $ = _c(16);
  const {
    children,
    icon,
    href,
    target,
    depth
  } = t0;
  const pathname = usePathname();
  const isLinkActive = pathname === href;
  if (depth > 1) {
    throw new Error("Level greater than 1 not supported");
  }
  const t1 = depth === 1 ? styles.depth1 : "";
  const t2 = `${styles.link} ${isLinkActive ? styles.active : ""} outline-inside`;
  let t3;
  if ($[0] !== depth) {
    t3 = depth > 0 ? <span className={styles.subpointIcon}><IconSystem icon="subpoint" variant="filled" /></span> : null;
    $[0] = depth;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== children) {
    t4 = <span>{children}</span>;
    $[2] = children;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== icon) {
    t5 = <span className={styles.icon} aria-hidden="true">{icon}</span>;
    $[4] = icon;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== href || $[7] !== t2 || $[8] !== t3 || $[9] !== t4 || $[10] !== t5 || $[11] !== target) {
    t6 = <Link className={t2} href={href} target={target}>{t3}{t4}{t5}</Link>;
    $[6] = href;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = target;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t1 || $[14] !== t6) {
    t7 = <NavigationListItem className={t1}>{t6}</NavigationListItem>;
    $[13] = t1;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}
export function NavigationListNodeItem(t0) {
  const $ = _c(10);
  const {
    children,
    title,
    depth
  } = t0;
  const [expanded, toggleExpanded] = useToggle(false);
  if (depth > 1) {
    throw new Error("Level greater than 1 not supported");
  }
  const t1 = expanded ? styles.expanded : "";
  let t2;
  if ($[0] !== expanded || $[1] !== title || $[2] !== toggleExpanded) {
    t2 = <NavigationButton text={title} className={`${styles.button} button-link`} onClick={toggleExpanded} expanded={expanded} />;
    $[0] = expanded;
    $[1] = title;
    $[2] = toggleExpanded;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children) {
    t3 = <NavigationList className={styles.nestedNavigationList}>{children}</NavigationList>;
    $[4] = children;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <NavigationListItem className={t1}>{t2}{t3}</NavigationListItem>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
export function NavigationListCustomItem(t0) {
  const $ = _c(3);
  const {
    className,
    children,
    depth
  } = t0;
  if (depth > 1) {
    throw new Error("Level greater than 1 not supported");
  }
  const t1 = `${depth === 1 ? styles.depth1 : ""} ${className ? className : ""}`;
  let t2;
  if ($[0] !== children || $[1] !== t1) {
    t2 = <NavigationListItem className={t1}>{children}</NavigationListItem>;
    $[0] = children;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}